import type { RenderIconNode as RenderIconNodeModel } from "../../../util/src/layout-compiler/types.js";
import { resolveMuiIconComponent } from "../components/MuiIconResolver.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";
import { useRuntimeNodeProps } from "./useRuntimeElementRef.js";

type RenderIconNodeProps = {
  node: RenderIconNodeModel;
  scale: number;
};

function composeTransform(
  rotation: number,
  styleTransform?: unknown,
): string | undefined {
  const rotationTransform =
    rotation !== 0 ? `rotate(${rotation}deg)` : undefined;
  const extra =
    typeof styleTransform === "string" && styleTransform.trim().length > 0
      ? styleTransform
      : undefined;

  if (rotationTransform && extra) return `${rotationTransform} ${extra}`;
  return rotationTransform ?? extra;
}

export function RenderIconNode({ node, scale }: RenderIconNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const clipStyle = getRenderNodeClipStyle(node, scale);
  const Icon = resolveMuiIconComponent(node.props.iconName ?? "Star");
  const size = node.props.size ?? Math.min(node.width, node.height);

  if (!Icon) {
    return null;
  }

  return (
    <div
      {...runtimeProps}
      style={{
        ...(node.style.position === "relative"
          ? {
              position: "relative",
              flexShrink: 0,
            }
          : {
              position: "absolute",
              left: node.x * scale,
              top: node.y * scale,
            }),
        width: node.width * scale,
        height: node.height * scale,
        zIndex: node.zIndex,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color:
          node.props.color ??
          (node.style.color as string | undefined) ??
          "#111111",
        opacity: node.style.opacity as number | undefined,
        boxSizing: "border-box",
        transform: composeTransform(node.rotation, node.style?.transform),
        transformOrigin: "center center",
        ...clipStyle,
      }}
    >
      <Icon sx={{ fontSize: size * scale, color: "inherit" }} />
    </div>
  );
}
